import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, ChevronRight, ChevronDown, FolderTree, RefreshCw, X } from 'lucide-react';
import MediaBrowser from '@/components/MediaBrowser';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';

const Categories = ({ user }) => {
  const [categories, setCategories] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteFromGHL, setDeleteFromGHL] = useState(true); // Checkbox state for GHL deletion
  const [formData, setFormData] = useState({ 
    name: '', 
    parent_id: null,
    slug: '',
    image: '',
    seo_title: '',
    seo_description: '',
    collection_type: 'manual',
    conditions: [],
    condition_logic: 'all'
  });
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchHierarchy();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      console.log('Categories response:', response.data);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      console.error('Error response:', error.response);
      toast.error(`Failed to fetch categories: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchy = async () => {
    try {
      const response = await axiosInstance.get('/categories/hierarchy');
      console.log('Hierarchy response:', response.data);
      setHierarchy(response.data);
      // Auto-expand all categories by default
      const allIds = new Set();
      const collectIds = (items, visited = new Set()) => {
        items.forEach(item => {
          // Prevent infinite loops from circular references
          if (visited.has(item.id)) {
            console.warn('Circular reference detected in category:', item.name, item.id);
            return;
          }
          visited.add(item.id);
          allIds.add(item.id);
          if (item.children && item.children.length > 0) {
            collectIds(item.children, visited);
          }
        });
      };
      try {
        collectIds(response.data);
      } catch (e) {
        console.error('Error collecting category IDs:', e);
      }
      setExpandedCategories(allIds);
    } catch (error) {
      console.error('Failed to fetch hierarchy:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axiosInstance.put(`/categories/${editingCategory.id}`, formData);
        toast.success('Category updated successfully');
      } else {
        await axiosInstance.post('/categories', formData);
        toast.success('Category created successfully');
      }
      setShowDialog(false);
      setEditingCategory(null);
      setFormData({ 
        name: '', 
        parent_id: null,
        image: '',
        seo_title: '',
        seo_description: '',
        collection_type: 'manual',
        conditions: [],
        condition_logic: 'all'
      });
      fetchCategories();
      fetchHierarchy();
    } catch (error) {
      console.error('Failed to save category:', error);
      console.error('Error response:', error.response);
      toast.error(`Failed to save category: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    // Generate slug if it doesn't exist
    const slug = category.slug || category.name.toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData({ 
      name: category.name, 
      parent_id: category.parent_id,
      slug: slug,
      image: category.image || '',
      seo_title: category.seo_title || '',
      seo_description: category.seo_description || '',
      collection_type: category.collection_type || 'manual',
      conditions: category.conditions || [],
      condition_logic: category.condition_logic || 'all'
    });
    setShowDialog(true);
  };

  const handleAddSubcategory = (parentId) => {
    setFormData({ 
      name: '', 
      parent_id: parentId,
      slug: '',
      image: '',
      seo_title: '',
      seo_description: '',
      collection_type: 'manual',
      conditions: [],
      condition_logic: 'all'
    });
    setShowDialog(true);
  };

  const handleDelete = async () => {
    try {
      const response = await axiosInstance.delete(`/categories/${deleteId}?delete_from_ghl=${deleteFromGHL}`);
      
      // Show success message with GHL status
      let message = 'Category deleted successfully';
      if (response.data.ghl_delete_status?.deleted) {
        message += ' and removed from ExcaliburCRM';
      } else if (response.data.ghl_delete_status?.message) {
        message += ` (${response.data.ghl_delete_status.message})`;
      }
      toast.success(message);
      
      fetchCategories();
      fetchHierarchy();
    } catch (error) {
      toast.error('Failed to delete category');
    } finally {
      setDeleteId(null);
      setDeleteFromGHL(true); // Reset checkbox for next time
    }
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleSyncToGHL = async () => {
    setSyncing(true);
    try {
      const response = await axiosInstance.post('/categories/sync-to-ghl');
      toast.success(`Pushed ${response.data.synced} categories to Excalibur`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} categories failed to push`);
      }
      fetchCategories();
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push categories');
    } finally {
      setSyncing(false);
    }
  };

  const handleImportFromGHL = async () => {
    setSyncing(true);
    try {
      const response = await axiosInstance.post('/categories/import-from-ghl');
      
      if (response.data.imported === 0 && response.data.skipped === 0) {
        toast.info('No collections found in Excalibur');
      } else if (response.data.imported === 0) {
        toast.info(`All ${response.data.total} collections from Excalibur are already here!`);
      } else if (response.data.imported === 1) {
        // Find which one was imported from the response if available, or just say "1 new"
        toast.success(`✨ Imported 1 new collection!`);
      } else {
        toast.success(`✨ Imported ${response.data.imported} new collections!`);
      }
      
      if (response.data.skipped > 0 && response.data.imported > 0) {
        toast.info(`${response.data.skipped} collections already existed`);
      }
      
      fetchCategories();
      fetchHierarchy();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import categories');
    } finally {
      setSyncing(false);
    }
  };

  const handleMediaSelect = (url) => {
    setFormData(prev => ({ ...prev, image: url }));
    setShowMediaBrowser(false);
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: 'title', operator: 'contains', value: '' }]
    }));
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const updateCondition = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((cond, i) => 
        i === index ? { ...cond, [key]: value } : cond
      )
    }));
  };


  const renderCategoryTree = (items, depth = 0) => {
    return items.map((category) => {
      const hasChildren = category.children && category.children.length > 0;
      const isExpanded = expandedCategories.has(category.id);
      
      return (
        <div key={category.id} style={{ marginLeft: `${depth * 24}px` }}>
          <div
            className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg border border-slate-200 mb-2 transition-colors"
            data-testid={`category-item-${category.id}`}
          >
            <div className="flex items-center gap-2 flex-1">
              {hasChildren ? (
                <button
                  onClick={() => toggleExpand(category.id)}
                  className="p-1 hover:bg-slate-200 rounded transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-slate-600" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-600" />
                  )}
                </button>
              ) : (
                <div className="w-6" />
              )}
              <FolderTree className="w-4 h-4 text-indigo-600" />
              <span className="font-medium text-slate-800">{category.name}</span>
              {!category.ghl_collection_id ? (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">
                  NEW
                </span>
              ) : category.needs_resync ? (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">
                  Edited
                </span>
              ) : (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                  Synced
                </span>
              )}
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAddSubcategory(category.id)}
                  data-testid={`add-subcategory-${category.id}`}
                  title="Add subcategory"
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(category)}
                  data-testid={`edit-category-${category.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteId(category.id)}
                  data-testid={`delete-category-${category.id}`}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          {hasChildren && isExpanded && renderCategoryTree(category.children, depth + 1)}
        </div>
      );
    });
  };

  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  return (
    <div data-testid="categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Category Management</h1>
          <p className="page-subtitle">Organize products with nested categories</p>
        </div>
        <div className="flex gap-3">
          {canEdit && (
            <>
              <Button
                onClick={handleImportFromGHL}
                disabled={syncing}
                variant="outline"
                data-testid="import-from-ghl-button"
                className="mr-2"
              >
                ⬇️ Import from Excalibur
              </Button>
              <Button
                onClick={handleSyncToGHL}
                disabled={syncing}
                variant="outline"
                data-testid="sync-categories-to-ghl-button"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Pushing...' : 'Push to Excalibur'}
              </Button>
              <Button onClick={() => setShowDialog(true)} data-testid="add-category-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Root Category
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Loading categories...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12" data-testid="no-categories-message">
            <FolderTree className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            <p className="text-slate-500 mb-4">No categories yet</p>
            {canEdit && (
              <Button onClick={() => setShowDialog(true)}>Create Your First Category</Button>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">How Categories Work</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Products added to a subcategory automatically appear in parent categories</li>
                <li>• Each category syncs to Excalibur as a separate collection</li>
                <li>• Click the + button to add subcategories</li>
                <li>• Sync categories to Excalibur before syncing products</li>
              </ul>
            </div>
            {hierarchy.length > 0 ? (
              renderCategoryTree(hierarchy)
            ) : (
              <div>
                <p className="text-amber-600 mb-4">⚠️ Circular reference detected in hierarchy. Showing flat list:</p>
                <ul className="space-y-2">
                  {categories.map(cat => (
                    <li key={cat.id} className="p-3 border rounded flex justify-between items-center">
                      <span>{cat.name} {cat.parent_id && `(child of ${categories.find(c => c.id === cat.parent_id)?.name})`}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(cat)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleteId(cat.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog
        open={showDialog && !showMediaBrowser}
        onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setEditingCategory(null);
            setFormData({ 
              name: '', 
              parent_id: null,
              image: '',
              seo_title: '',
              seo_description: '',
              collection_type: 'manual',
              conditions: [],
              condition_logic: 'all'
            });
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="category-dialog-title">
              {editingCategory ? 'Edit Category' : formData.parent_id ? 'Add Subcategory' : 'Add Root Category'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="category-name">Category Name *</Label>
                <Input
                  id="category-name"
                  data-testid="category-name-input"
                  value={formData.name}
                  onChange={(e) => {
                    const newName = e.target.value;
                    // Auto-generate slug from name
                    const slug = newName.toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/[\s_]+/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-+|-+$/g, '');
                    setFormData({ ...formData, name: newName, slug: slug });
                  }}
                  required
                  placeholder="e.g., Hunting Bows, Accessories"
                />
              </div>

              <div>
                <Label htmlFor="category-slug">URL Slug (Handle) *</Label>
                <Input
                  id="category-slug"
                  value={formData.slug || ''}
                  onChange={(e) => {
                    const slug = e.target.value.toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/[\s_]+/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-+|-+$/g, '');
                    setFormData({ ...formData, slug: slug });
                  }}
                  required
                  placeholder="e.g., hunting-bows, accessories"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Auto-generated from name. Used in URL: /products-list/collections/<strong>{formData.slug || 'category-slug'}</strong>
                </p>
              </div>

              <div>
                <Label htmlFor="parent-category">Parent Category</Label>
                <select
                  id="parent-category"
                  value={formData.parent_id || ''}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
                  className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">-- Root Category (No Parent) --</option>
                  {categories
                    .filter(cat => !editingCategory || cat.id !== editingCategory.id)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Select a parent to make this a subcategory. Leave blank for root level.
                </p>
              </div>

              {formData.parent_id && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    ℹ️ Products added to <strong>{formData.name || 'this category'}</strong> will automatically be added to <strong>
                      {categories.find(c => c.id === formData.parent_id)?.name}</strong> collection as well.
                  </p>
                </div>
              )}

              <div>
                <Label>Collection Type *</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="collection_type"
                      value="manual"
                      checked={formData.collection_type === 'manual'}
                      onChange={(e) => setFormData({ ...formData, collection_type: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Manual - Select products manually</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="collection_type"
                      value="smart"
                      checked={formData.collection_type === 'smart'}
                      onChange={(e) => setFormData({ ...formData, collection_type: e.target.value })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Smart - Auto-match by conditions</span>
                  </label>
                </div>
              </div>

              {formData.collection_type === 'smart' && (
                <div className="space-y-3">
                  <Label>Conditions</Label>
                  <div className="space-y-2">
                    {formData.conditions.map((condition, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <select
                          value={condition.field}
                          onChange={(e) => updateCondition(index, 'field', e.target.value)}
                          className="w-32 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                        >
                          <option value="title">Title</option>
                          <option value="variant_title">Variant Title</option>
                          <option value="price">Price</option>
                          <option value="inventory">Inventory</option>
                        </select>

                        <select
                          value={condition.operator}
                          onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                          className="w-40 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                        >
                          <option value="equals">Equals</option>
                          <option value="not_equals">Not Equals</option>
                          <option value="contains">Contains</option>
                          <option value="not_contains">Does Not Contain</option>
                          <option value="greater_than">Greater Than</option>
                          <option value="less_than">Less Than</option>
                        </select>

                        <Input
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                          placeholder="Value"
                          className="flex-1"
                        />

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCondition(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCondition}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Condition
                  </Button>

                  {formData.conditions.length > 1 && (
                    <div>
                      <Label>Match Logic</Label>
                      <select
                        value={formData.condition_logic}
                        onChange={(e) => setFormData({ ...formData, condition_logic: e.target.value })}
                        className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm mt-2"
                      >
                        <option value="all">ALL conditions (AND)</option>
                        <option value="any">ANY condition (OR)</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label>Collection Image</Label>
                <p className="text-xs text-slate-500 mb-2">Optional image for this collection</p>
                {formData.image ? (
                  <div className="relative inline-block">
                    <img src={formData.image} alt="Category" className="h-24 w-24 object-cover rounded border" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute -top-2 -right-2"
                      onClick={() => setFormData({ ...formData, image: '' })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" onClick={() => setShowMediaBrowser(true)}>
                    Choose Image
                  </Button>
                )}
              </div>

              <div>
                <Label htmlFor="seo-title">SEO Title</Label>
                <Input
                  id="seo-title"
                  value={formData.seo_title}
                  onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                  placeholder="Optional - Max 70 characters"
                  maxLength={70}
                />
                <p className="text-xs text-slate-500 mt-1">{formData.seo_title.length}/70</p>
              </div>

              <div>
                <Label htmlFor="seo-description">SEO Description</Label>
                <Textarea
                  id="seo-description"
                  value={formData.seo_description}
                  onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                  placeholder="Optional - Max 155 characters"
                  maxLength={155}
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-1">{formData.seo_description.length}/155</p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                data-testid="cancel-category-button"
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="save-category-button">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MediaBrowser
        isOpen={showMediaBrowser}
        onClose={() => setShowMediaBrowser(false)}
        onSelect={handleMediaSelect}
        allowMultiple={false}
      />

      <AlertDialog open={deleteId !== null} onOpenChange={() => {
        setDeleteId(null);
        setDeleteFromGHL(true); // Reset checkbox when dialog closes
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this category? This action cannot be undone.
              Subcategories will not be deleted but will become root categories.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Checkbox for ExcaliburCRM deletion */}
          <div className="flex items-center gap-2 py-3">
            <input
              type="checkbox"
              id="delete-category-from-ghl"
              checked={deleteFromGHL}
              onChange={(e) => setDeleteFromGHL(e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
            />
            <label htmlFor="delete-category-from-ghl" className="text-sm text-slate-700 cursor-pointer">
              Also delete from ExcaliburCRM
            </label>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-category-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-category-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Categories;
